import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { 
  CloudArrowUpIcon, 
  PhotoIcon, 
  SparklesIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  Squares2X2Icon,
  PaintBrushIcon
} from '@heroicons/react/24/outline';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://api.mythicalarc.com/api'
  : 'http://localhost:8000/api';

const ImageEffectsStudio = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [availableEffects, setAvailableEffects] = useState({});
  const [effectCategories, setEffectCategories] = useState({});
  const [selectedEffect, setSelectedEffect] = useState('');
  const [intensity, setIntensity] = useState(1.0);
  const [processing, setProcessing] = useState(false);
  const [processedResults, setProcessedResults] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [activeTab, setActiveTab] = useState('create');
  const [batchMode, setBatchMode] = useState(false);
  const [selectedEffects, setSelectedEffects] = useState([]);

  useEffect(() => {
    loadAvailableEffects();
    if (activeTab === 'gallery') {
      loadGallery();
    }
  }, [activeTab]);

  const loadAvailableEffects = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/image-effects/effects/available`);
      setAvailableEffects(response.data.effects);
      setEffectCategories(response.data.categories);
    } catch (error) {
      console.error('Failed to load effects:', error);
    }
  };

  const loadGallery = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/image-effects/effects/gallery`);
      setGallery(response.data.images);
    } catch (error) {
      console.error('Failed to load gallery:', error);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
      setProcessedResults([]); // Clear previous results
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.bmp', '.webp']
    },
    maxSize: 50 * 1024 * 1024, // 50MB
    multiple: false
  });

  const applyEffect = async () => {
    if (!selectedImage || (!selectedEffect && !batchMode)) return;

    setProcessing(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedImage);
      formData.append('intensity', intensity.toString());

      let response;
      
      if (batchMode && selectedEffects.length > 0) {
        // Batch processing
        selectedEffects.forEach(effect => {
          formData.append('effect_names', effect);
        });
        response = await axios.post(`${API_BASE_URL}/image-effects/effects/batch`, formData);
        
        if (response.data.success) {
          setProcessedResults(response.data.results);
        }
      } else {
        // Single effect
        formData.append('effect_name', selectedEffect);
        response = await axios.post(`${API_BASE_URL}/image-effects/effects/apply`, formData);
        
        if (response.data.success) {
          setProcessedResults([response.data.result]);
        }
      }
    } catch (error) {
      console.error('Effect processing failed:', error);
      alert(error.response?.data?.detail || 'Effect processing failed');
    } finally {
      setProcessing(false);
    }
  };

  const downloadImage = async (downloadId, effectName) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/image-effects/effects/download/${downloadId}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${effectName}_effect_${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed');
    }
  };

  const toggleEffectSelection = (effectName) => {
    setSelectedEffects(prev => 
      prev.includes(effectName)
        ? prev.filter(e => e !== effectName)
        : [...prev, effectName]
    );
  };

  const clearImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setProcessedResults([]);
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
  };

  const getEffectIcon = (category) => {
    switch (category) {
      case 'artistic': return '🎨';
      case 'filter': return '📸';
      case 'stylized': return '✨';
      default: return '🖼️';
    }
  };

  const EffectCard = ({ effectName, effectInfo, isSelected, onClick }) => (
    <div
      onClick={onClick}
      className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
        isSelected 
          ? 'border-blue-600 bg-blue-50' 
          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center">
          <span className="text-xl mr-2">
            {getEffectIcon(effectInfo.category)}
          </span>
          <h3 className="font-medium text-gray-900">{effectInfo.name}</h3>
        </div>
        {effectInfo.requires_ai && (
          <SparklesIcon className="w-4 h-4 text-purple-500" title="AI Enhanced" />
        )}
      </div>
      <p className="text-sm text-gray-600">{effectInfo.description}</p>
      <div className="mt-2">
        <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
          effectInfo.category === 'artistic' ? 'bg-purple-100 text-purple-800' :
          effectInfo.category === 'filter' ? 'bg-green-100 text-green-800' :
          'bg-blue-100 text-blue-800'
        }`}>
          {effectInfo.category}
        </span>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Image Effects Studio</h1>
        <p className="text-lg text-gray-600">
          Transform your images with AI-powered and artistic effects
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'create', name: 'Create Effects', icon: PaintBrushIcon },
            { id: 'gallery', name: 'Gallery', icon: Squares2X2Icon }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`group inline-flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className={`mr-2 h-5 w-5 ${
                activeTab === tab.id ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-500'
              }`} />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Create Effects Tab */}
      {activeTab === 'create' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Upload Section */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Upload Image</h2>
              
              {!selectedImage ? (
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors duration-200 ${
                    isDragActive
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <input {...getInputProps()} />
                  <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  {isDragActive ? (
                    <p className="text-lg text-blue-600">Drop the image here...</p>
                  ) : (
                    <div>
                      <p className="text-lg text-gray-600 mb-2">
                        Drag & drop an image here, or click to select
                      </p>
                      <p className="text-sm text-gray-500">
                        Supports JPEG, PNG, WebP (Max 50MB)
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Selected"
                      className="w-full h-48 object-cover rounded-lg border"
                    />
                    <button
                      onClick={clearImage}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="text-sm text-gray-600">
                    <p><strong>File:</strong> {selectedImage.name}</p>
                    <p><strong>Size:</strong> {(selectedImage.size / (1024 * 1024)).toFixed(2)} MB</p>
                  </div>
                </div>
              )}

              {/* Batch Mode Toggle */}
              {selectedImage && (
                <div className="mt-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={batchMode}
                      onChange={(e) => setBatchMode(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Batch Mode (Multiple Effects)</span>
                  </label>
                </div>
              )}

              {/* Intensity Slider */}
              {selectedImage && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Effect Intensity: {intensity.toFixed(1)}
                  </label>
                  <input
                    type="range"
                    min="0.1"
                    max="2.0"
                    step="0.1"
                    value={intensity}
                    onChange={(e) => setIntensity(parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Subtle</span>
                    <span>Normal</span>
                    <span>Intense</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Effects Selection */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-gray-900">Choose Effects</h2>
                {batchMode && (
                  <span className="text-sm text-blue-600">
                    {selectedEffects.length} selected
                  </span>
                )}
              </div>

              {Object.entries(effectCategories).map(([category, effects]) => (
                <div key={category} className="mb-6">
                  <h3 className="text-md font-medium text-gray-800 mb-3 capitalize">
                    {getEffectIcon(category)} {category} Effects
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {effects.map((effectName) => (
                      <EffectCard
                        key={effectName}
                        effectName={effectName}
                        effectInfo={availableEffects[effectName]}
                        isSelected={
                          batchMode 
                            ? selectedEffects.includes(effectName)
                            : selectedEffect === effectName
                        }
                        onClick={() => {
                          if (batchMode) {
                            toggleEffectSelection(effectName);
                          } else {
                            setSelectedEffect(effectName);
                          }
                        }}
                      />
                    ))}
                  </div>
                </div>
              ))}

              {/* Apply Button */}
              {selectedImage && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <button
                    onClick={applyEffect}
                    disabled={
                      processing || 
                      (!batchMode && !selectedEffect) || 
                      (batchMode && selectedEffects.length === 0)
                    }
                    className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed py-3 text-lg"
                  >
                    {processing ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Processing...
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <SparklesIcon className="w-5 h-5 mr-2" />
                        Apply {batchMode ? `${selectedEffects.length} Effects` : 'Effect'}
                      </div>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Results Section */}
      {processedResults.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            Processed Images ({processedResults.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {processedResults.map((result, index) => (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="aspect-square bg-gray-100">
                  <img
                    src={result.thumbnail}
                    alt={result.effect_display_name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-medium text-gray-900 mb-2">
                    {result.effect_display_name}
                  </h3>
                  <div className="text-sm text-gray-600 mb-3">
                    <p>Size: {result.file_size_mb} MB</p>
                    <p>Dimensions: {result.processed_size?.[0]} × {result.processed_size?.[1]}</p>
                  </div>
                  <button
                    onClick={() => downloadImage(result.download_id, result.effect_name)}
                    className="w-full btn-primary text-sm py-2"
                  >
                    <ArrowDownTrayIcon className="w-4 h-4 mr-2 inline" />
                    Download
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Gallery Tab */}
      {activeTab === 'gallery' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-medium text-gray-900">Effects Gallery</h2>
            <button
              onClick={loadGallery}
              className="btn-secondary text-sm"
            >
              Refresh
            </button>
          </div>

          {gallery.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {gallery.map((item, index) => (
                <div key={index} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                  <div className="aspect-square bg-gray-100 flex items-center justify-center">
                    <PhotoIcon className="w-12 h-12 text-gray-400" />
                  </div>
                  <div className="p-3">
                    <h3 className="font-medium text-gray-900 text-sm mb-1 capitalize">
                      {item.effect_name.replace('_', ' ')}
                    </h3>
                    <div className="text-xs text-gray-600 mb-2">
                      <p>{item.file_size_mb} MB</p>
                      <p>{new Date(item.created_at).toLocaleDateString()}</p>
                    </div>
                    <button
                      onClick={() => downloadImage(item.download_id, item.effect_name)}
                      className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs py-1 px-2 rounded transition-colors"
                    >
                      <ArrowDownTrayIcon className="w-3 h-3 mr-1 inline" />
                      Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <PhotoIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No images yet</h3>
              <p className="text-gray-500">
                Process some images to see them appear in your gallery
              </p>
            </div>
          )}
        </div>
      )}

      {/* Tips Section */}
      <div className="bg-blue-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-blue-900 mb-4">
          💡 Tips for Best Results
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
          <div>
            <h4 className="font-medium mb-2">Image Quality:</h4>
            <ul className="space-y-1">
              <li>• Use high-resolution images (1024px+ recommended)</li>
              <li>• Clear, well-lit photos work best</li>
              <li>• Avoid heavily compressed images</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium mb-2">Effect Selection:</h4>
            <ul className="space-y-1">
              <li>• Start with intensity 1.0, then adjust</li>
              <li>• AI effects (✨) provide more dramatic results</li>
              <li>• Batch mode lets you compare multiple styles</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageEffectsStudio;